import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';

const navLinks = [
  { href: '#hero', label: 'Home' },
  { href: '#why', label: 'Why Us' },
  { href: '#crops', label: 'Crops' },
  { href: '#how-it-works', label: 'How It Works' },
  { href: '#income', label: 'Opportunity' },
  { href: '#faq', label: 'FAQ' },
];

const Header = ({ onOpenEnquiry }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const ids = navLinks.map((l) => l.href.slice(1));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (href) => {
    if (href === '#hero') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-lg shadow-black/5' 
          : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="container mx-auto px-4 lg:px-8">
        <nav className="flex items-center justify-between h-18 lg:h-22 py-3">
          {/* Logo - Text based: Apna (white/dark) Farm (green) */}
          <button
            onClick={scrollToTop}
            className="flex items-center group"
            data-testid="header-logo"
          >
            <span className={`text-3xl lg:text-4xl font-black tracking-tight transition-colors ${
              isScrolled ? 'text-[#004534]' : 'text-white'
            }`}>
              Apna<span className="text-[#c4e538]">Farm</span>
            </span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => {
              const active = activeSection === link.href.slice(1);
              return (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className={`relative text-[15px] font-semibold transition-colors py-1 ${
                    isScrolled
                      ? active ? 'text-[#004534]' : 'text-[#004534]/70 hover:text-[#004534]'
                      : active ? 'text-white' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-0.5 left-0 h-0.5 rounded-full bg-[#c4e538] transition-all duration-300 ${
                      active ? 'w-full opacity-100' : 'w-0 opacity-0'
                    }`}
                  />
                </button>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button
              onClick={onOpenEnquiry}
              className={`font-bold rounded-full px-8 py-3 text-base transition-all hover:scale-105 ${
                isScrolled
                  ? 'bg-[#004534] text-white hover:bg-[#0C6951]'
                  : 'bg-white text-[#004534] hover:bg-white/90'
              }`}
              data-testid="header-cta"
            >
              Enquire Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              isScrolled ? 'text-[#004534]' : 'text-white'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </nav>
      </div>

      {/* Scroll progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-transparent">
        <div
          className="h-full bg-gradient-to-r from-[#c4e538] to-[#0C6951] transition-[width] duration-150 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <div className="container mx-auto px-4 lg:px-8">
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-4 right-4 bg-white rounded-2xl shadow-2xl p-6 animate-scale-in">
            <div className="space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="flex w-full text-left text-[#004534] py-4 px-4 rounded-xl hover:bg-[#004534]/5 transition-colors font-semibold text-lg"
                >
                  {link.label}
                </button>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <Button
                onClick={() => { onOpenEnquiry(); setIsMobileMenuOpen(false); }}
                className="w-full bg-[#004534] text-white hover:bg-[#0C6951] font-bold rounded-full py-4 text-lg"
              >
                Enquire Now
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
